import { useRef, useEffect, useCallback } from 'react';
import { useFlushOnPageHide } from '@/hooks/use-flush-on-page-hide';
import { useMutation } from '@apollo/client';
import { UPDATE_PROJECT } from '@/queries/queries';

interface Options {
  projectId: string | null;
  /** The live editor state blob ({ subtitle, logo, font_id }). */
  config: Record<string, unknown>;
  /** What the project loaded with — seeds the diff baseline so the load echo
   *  is never written back. */
  loadedConfig?: Record<string, unknown> | null;
  enabled: boolean;
  delay?: number;
}

type PendingWrite = { projectId: string; config: Record<string, unknown>; serialized: string };

/** JSON.stringify with sorted keys: Postgres jsonb reorders object keys, so a
 *  naive stringify of the loaded config never equals the reconstructed blob
 *  and every page load produced one spurious (same-values) write. */
export function stableSerialize(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(stableSerialize).join(',')}]`;
  if (value && typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>)
      .sort(([a], [b]) => (a < b ? -1 : 1))
      .map(([k, v]) => `${JSON.stringify(k)}:${stableSerialize(v)}`);
    return `{${entries.join(',')}}`;
  }
  return JSON.stringify(value) ?? 'null';
}

/** Debounced persistence of the subtitle/logo editor config to
 *  project.subtitle_config. Same invariant as every other editor writer:
 *  a pending write is flushed on unmount, never dropped. Project-scoped, so
 *  there is no clip-switch case. No refetch — the write is the echo. */
export function useAutoSaveSubtitleConfig({
  projectId, config, loadedConfig = null, enabled, delay = 600,
}: Options) {
  const [updateProject, { loading: saving }] = useMutation(UPDATE_PROJECT);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingRef = useRef<PendingWrite | null>(null);
  const lastSavedRef = useRef<string | null>(null);

  // Seed once from the loaded value (or its absence).
  if (lastSavedRef.current === null) {
    lastSavedRef.current = loadedConfig ? stableSerialize(loadedConfig) : '';
  }

  const performSave = useCallback((write: PendingWrite) => {
    updateProject({
      variables: { id: write.projectId, input: { subtitle_config: write.config } },
    })
      .then(() => {
        lastSavedRef.current = write.serialized;
      })
      .catch((err) => console.error('Failed to persist subtitle config', err));
  }, [updateProject]);

  const firePending = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    const pending = pendingRef.current;
    pendingRef.current = null;
    if (pending) performSave(pending);
  }, [performSave]);
  // Tab close never unmounts: fire the pending write as a keepalive request.
  useFlushOnPageHide(firePending);
  const firePendingRef = useRef(firePending);
  useEffect(() => {
    firePendingRef.current = firePending;
  }, [firePending]);

  useEffect(() => {
    if (!enabled || !projectId) return;
    const serialized = stableSerialize(config);
    if (serialized === lastSavedRef.current) {
      // Nothing to save — and any pending write is stale (e.g. the defaults
      // armed on first render before the restore effect landed). Without this
      // the unmount flush would write that stale blob over the saved config.
      pendingRef.current = null;
      return;
    }

    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    pendingRef.current = { projectId, config, serialized };
    timeoutRef.current = setTimeout(() => {
      timeoutRef.current = null;
      const pending = pendingRef.current;
      pendingRef.current = null;
      if (pending) performSave(pending);
    }, delay);
    // Clears only the timer; pendingRef survives for the unmount flush.
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [config, projectId, enabled, delay, performSave]);

  // Unmount: flush, never drop.
  useEffect(() => () => firePendingRef.current(), []);

  return { saving };
}
